import createRandomID from "Utilities/RandomID";

export const composeInitialRespondents = (
  state,
  signupItem,
  isSignedUpForCurrentItem
) => {
  if (!state.user) return []; // this is if the user *just* clicked the "Not You" button and user data was removed while the Signup Item is expanded.

  const signupItemID = signupItem.ID;
  const userSignupItem = getUserSignupItem(state, signupItemID);

  let respondents = [];

  // 1. add the currently logged in user (or anonymousUser) to the respondents array
  const primaryUser = getPrimaryUser(state, userSignupItem);
  respondents.push(primaryUser);

  // 2. add their household users to the respondents array
  const householdUsers = getHouseholdUsers(state, userSignupItem);
  householdUsers.forEach((user) => {
    respondents.push({ ...user });
  });

  // 3. add "Additional Name" entries to the respondents array
  const additionalNames = getAdditionalNames(userSignupItem);
  additionalNames.forEach((person) => {
    respondents.push({ ...person });
  });

  // 4. handle if any respondents should be disabled
  disableRespondentsSignedUpForOtherItems(state, signupItem, respondents);

  // 5. handle pre-selecting appropriate respondents
  preselectRespondents(
    isSignedUpForCurrentItem,
    userSignupItem,
    respondents,
    state.memberRespondents
  );

  /*
  NOTE from MF 8/24/23: commenting out this unSavedAdditionalNames code, as it introduced a bug where
  clicking "Modify" and then "Submit" would duplicate additionalNames signed up. But leaving this
  here as reference since the bug it was fixing now exists again (adding a HH member wipes out any
  unsaved AdditionalNames that had been entered)
  */
  // const unSavedAdditionalNames = getUnsavedSignedUpAdditionalNames(
  //   state.memberRespondents,
  //   respondents
  // );

  // const allRespondents = [...respondents, ...unSavedAdditionalNames];
  const allRespondents = [...respondents];

  return allRespondents;
};

function getUserSignupItem(state, signupItemID) {
  const { userSignupItems } = state;

  if (!userSignupItems) return null;
  if (Array.isArray(userSignupItems) && userSignupItems.length === 0)
    return null;

  return userSignupItems[signupItemID];
}

function getPrimaryUser(state, userSignupItem) {
  // (note: before this step is reached, the user is logged in, or has been prompted to fill out the new user form)

  const userIsNotLoggedIn = state.user.isAnonymousUser;
  if (userIsNotLoggedIn) {
    /* 
    For non-logged-in users, there are two scenarios we need to consider here: 
    1. They JUST submitted their signup. 
        - If this is the case, there will be a respondent in userSignupItems that has a userID.
    2. They have inputed their name & emailOrPhone, but have not signed up yet
        - If this is the case, we use the name & emailOrPhone values in state (and assign them a temporary random userID so things don't break)
    */

    const respondentWithUserID = userSignupItem?.respondents?.find(
      (r) => r.userID
    );
    if (respondentWithUserID) {
      return {
        ...respondentWithUserID,
        emailOrPhone: state.user.emailOrPhone,
        isAnonymousUser: true,
      };
    } else {
      return {
        name: state.user.name,
        emailOrPhone: state.user.emailOrPhone,
        isAnonymousUser: true,
        userID: createRandomID(8),
      };
    }
  } else {
    const userID = state.user.ID;

    // paymentStatus is added to the primary user by looking it up in userSignupItems
    const paymentStatus = userSignupItem?.respondents?.find(
      (r) => r.userID === userID
    )?.paymentStatus;
    const { isHoHOrSpouseOfHoH, extendedFieldResponses, wasJustAddedToHH } =
      state.user;
    return {
      name: `${state.user.firstName} ${state.user.lastName || ""}`,
      userID,
      paymentStatus,
      isHoHOrSpouseOfHoH,
      extendedFieldResponses,
      wasJustAddedToHH,
    };
  }
}

function getHouseholdUsers(state, userSignupItem) {
  const { canSignupHouseholdMembers, household } = state.user;

  if (!canSignupHouseholdMembers || !household) return [];

  return household.map((person) => {
    // the paymentStatus for each household member is looked up in userSignupItems
    const paymentStatus = userSignupItem?.respondents?.find(
      (r) => r.userID === person.userID
    )?.paymentStatus;

    return {
      ...person,
      paymentStatus,
    };
  });
}

function getAdditionalNames(userSignupItem) {
  // "Additional Names" are the manually typed names that were added to a signup by the signer-upper.
  // In this context, they are only present if modifying an existing signup where they were previously entered.

  if (!userSignupItem?.respondents) return [];

  const additionalNames = userSignupItem.respondents.filter(
    ({ isAdditionalName }) => Boolean(isAdditionalName)
  );

  return additionalNames.map((respondent) => {
    return {
      ...respondent,
      userID: respondent.userID || createRandomID(8),
    };
  });
}

function disableRespondentsSignedUpForOtherItems(
  state,
  signupItem,
  respondents
) {
  // This adds an "isDisabled" flag to any respondents that shouldn't be selectable.
  // This is based on the allowMultipleItemSignups setting and if they are signed up for other items.

  // if the allowMultipleItemSignups setting is on we don't need to worry about this.
  if (state.currentSignupList.options.allowMultipleItemSignups) return;

  // if there are no current signups we don't need to worry about this.
  const { userSignupItems } = state;
  if (!userSignupItems) return;
  if (Array.isArray(userSignupItems) && userSignupItems.length === 0) return;

  // this is a re-structuring of userSignupItems to filter out the current signup item and to make it easier to search.
  const allOtherItemSignups = [];
  Object.keys(state.userSignupItems).forEach((itemID) => {
    const isCurrentSignupItem = String(itemID) === String(signupItem.ID);
    if (!isCurrentSignupItem) {
      // push each respondent into `allOtherItemSignups`
      userSignupItems[itemID].respondents?.forEach((r) => {
        allOtherItemSignups.push({
          ...r,
          signupItemID: itemID,
        });
      });
    }
  });

  respondents.forEach((r) => {
    // we can ignore respondents without userIDs
    if (!r.userID) return;

    const otherSignup = allOtherItemSignups.find(
      (entry) => parseInt(entry.userID) === parseInt(r.userID)
    );

    if (otherSignup) {
      r.isDisabled = true;
      r.currentlySignedUpItemID = otherSignup.signupItemID;
    }
  });
}

function preselectRespondents(
  isSignedUpForCurrentItem,
  userSignupItem,
  respondents,
  memberRespondents
) {
  if (isSignedUpForCurrentItem) {
    // only pre-select those currently signed up (or if HH user was added in this session)
    respondents.forEach((r) => {
      const isChecked =
        r.isAdditionalName ||
        r.isAnonymousUser ||
        r.wasJustAddedToHH ||
        Boolean(
          userSignupItem?.respondents?.find((ar) => ar.userID === r.userID)
        );

      r.isChecked = isChecked;

      if (isChecked && r.wasJustAddedToHH) {
        r.wasJustAddedToHH = false;
      }
    });
  } else {
    // if not currently signed up for this item, only pre-select primary user (or if already checked)
    respondents.forEach((r, i) => {
      const memberRespondent = memberRespondents[r.userID] || {};
      const memberRespondentIsChecked = memberRespondent?.isChecked || false;
      const isChecked =
        r.isChecked ||
        r.wasJustAddedToHH ||
        (!r.isDisabled && memberRespondentIsChecked);
      r.isChecked = isChecked;
    });
  }
}

// function getUnsavedSignedUpAdditionalNames(memberRespondents, respondents) {
//   console.log("memberRespondents:", memberRespondents);
//   console.log("respondents:", respondents);
//   const signedUpAdditionalNames = Object.values(memberRespondents).filter(
//     (r) => r.isAdditionalName && r.isChecked
//   );
//   console.log("gonna add:", signedUpAdditionalNames);
//   return signedUpAdditionalNames;
// }
